import * as React from 'react';
import { useRender } from '@base-ui-components/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
	{
		variants: {
			variant: {
				default:
					'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
				destructive:
					'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
				outline:
					'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
				secondary:
					'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-9 px-4 py-2 has-[>svg]:px-3',
				sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
				lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
				icon: 'size-9',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

/**
 * Base UI's replacement for Radix's `asChild`/`Slot` pattern: instead of a
 * boolean flag plus a single child element, the element to render *as* is
 * passed directly via `render`. `useRender` merges this component's own
 * computed props (`className`, `data-slot`, the rest of `...props`) onto it
 * -- `render`'s own props win on conflicts except `className`/`style`
 * (concatenated) and event handlers (chained), matching what `asChild` used
 * to do. `<Button asChild><Link to="/">Text</Link></Button>` becomes
 * `<Button render={<Link to="/">Text</Link>} />` -- the link's own children
 * carry the label now, not Button's.
 */
function Button({
	className,
	variant,
	size,
	render,
	...props
}: React.ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		render?: React.ReactElement<Record<string, unknown>>;
	}) {
	return useRender({
		render,
		// Not a literal `<button />` default: oxlint's jsx-a11y rule flags a
		// bare JSX button element in source as unlabelled, even though every
		// real render always spreads `children`/`aria-label` onto it via
		// `props`. `defaultTagName` gets the same plain `<button>` fallback
		// without writing that literal.
		defaultTagName: 'button',
		props: {
			'data-slot': 'button',
			className: cn(buttonVariants({ variant, size, className })),
			...props,
		},
	});
}

export { Button, buttonVariants };
